import { getMessages } from "@/i18n/runtime";
import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { hugeZero, maxHuge, type HugeNumber } from "@/shared/numbers";
import { UPGRADE_MILESTONE_MULTIPLIER } from "@/features/hoverIdle/core/upgradeMilestones";
import type { HovGainUpgradePurchaseBlockedReason } from "../types";

export type HovGainUpgradeTooltipKind =
  | { readonly type: "global" }
  | { readonly type: "cell"; readonly displayIndex: number };

export interface HovGainUpgradeTooltipViewModel {
  readonly displayMode?: "full" | "state-only";
  readonly title: string;
  readonly region: string;
  readonly summary: string;
  readonly effectLabel: string;
  readonly effect: string;
  readonly afterLabel: string;
  readonly after: string;
  readonly costLabel: string;
  readonly cost: string;
  readonly stateLabel: string;
  readonly state: string;
}

export type HovGainUpgradeTooltipEffect =
  | { readonly type: "delta" }
  | { readonly type: "multiplier"; readonly multiplier: string }
  | { readonly type: "enhancement-levels"; readonly count: number };

export interface HovGainUpgradeTooltipInput {
  readonly kind: HovGainUpgradeTooltipKind;
  readonly currentHov: HugeNumber;
  readonly cost: HugeNumber;
  readonly canPurchase: boolean;
  readonly currentValue: HugeNumber;
  readonly nextValue: HugeNumber;
  readonly effect: HovGainUpgradeTooltipEffect;
  readonly purchaseCount?: number;
  /**
   * When the regular purchase will fire multiple times at once (Price Wall
   * simultaneous-purchase reward), the cost label/value reflect the total
   * cost paid for `purchaseCount` purchases. The one-cost plateau bulk path
   * continues to use `purchaseCount` alone with the existing batch format.
   */
  readonly simultaneousPurchase?: {
    readonly purchaseCount: number;
    readonly totalCost: HugeNumber;
  };
  /**
   * Player-facing enhancement level shown after the node title. 0 for HG
   * before any purchase and for a locked HnG cell.
   */
  readonly enhancementLevel: number;
}

export function buildHovGainUpgradeTooltipViewModel(
  input: HovGainUpgradeTooltipInput,
): HovGainUpgradeTooltipViewModel {
  const messages = getMessages().hovView.upgradeTooltip;
  const effect = formatHovGainUpgradeEffect(input);
  const stateCost = resolveTooltipStateCost(input);
  const missingHov = maxHuge(hugeZero(), stateCost.sub(input.currentHov));

  return {
    title: formatHovGainUpgradeTitle(input.kind, input.enhancementLevel),
    region: getMessages().hovView.region,
    summary: formatHovGainUpgradeSummary(input.kind),
    effectLabel: messages.effectLabel,
    effect,
    afterLabel: formatHovGainUpgradeAfterLabel(input.kind),
    after: messages.after(
      formatForContext(input.currentValue, "tooltip"),
      formatForContext(input.nextValue, "tooltip"),
    ),
    costLabel: formatHovGainUpgradeCostLabel(input, "tooltip"),
    cost: formatHovGainUpgradeTooltipCost(input, "tooltip"),
    stateLabel: messages.stateLabel,
    state: input.canPurchase
      ? messages.stateReady
      : messages.stateNeed(formatForContext(missingHov, "tooltip")),
  };
}

export interface HovGainUpgradeWallBlockedTooltipInput {
  readonly kind: HovGainUpgradeTooltipKind;
  readonly enhancementLevel: number;
  readonly reason: HovGainUpgradePurchaseBlockedReason;
}

export function buildHovGainUpgradeWallBlockedTooltipViewModel(
  input: HovGainUpgradeWallBlockedTooltipInput,
): HovGainUpgradeTooltipViewModel {
  const messages = getMessages().hovView.upgradeTooltip;
  return {
    displayMode: "state-only",
    title: formatHovGainUpgradeTitle(input.kind, input.enhancementLevel),
    region: getMessages().hovView.region,
    summary: formatHovGainUpgradeSummary(input.kind),
    effectLabel: messages.effectLabel,
    effect: "",
    afterLabel: formatHovGainUpgradeAfterLabel(input.kind),
    after: "",
    costLabel: messages.costLabel,
    cost: "",
    stateLabel: messages.stateLabel,
    state: formatHovGainUpgradeBlockedState(input.reason),
  };
}

function resolveTooltipStateCost(
  input: HovGainUpgradeTooltipInput,
): HugeNumber {
  return input.simultaneousPurchase?.totalCost ?? input.cost;
}

export function createLazyHovGainUpgradeTooltipViewModel(
  resolve: () => HovGainUpgradeTooltipViewModel,
): HovGainUpgradeTooltipViewModel {
  let resolved: HovGainUpgradeTooltipViewModel | null = null;
  const getResolved = (): HovGainUpgradeTooltipViewModel => {
    resolved ??= resolve();
    return resolved;
  };

  return {
    get title() {
      return getResolved().title;
    },
    get displayMode() {
      return getResolved().displayMode;
    },
    get region() {
      return getResolved().region;
    },
    get summary() {
      return getResolved().summary;
    },
    get effectLabel() {
      return getResolved().effectLabel;
    },
    get effect() {
      return getResolved().effect;
    },
    get afterLabel() {
      return getResolved().afterLabel;
    },
    get after() {
      return getResolved().after;
    },
    get costLabel() {
      return getResolved().costLabel;
    },
    get cost() {
      return getResolved().cost;
    },
    get stateLabel() {
      return getResolved().stateLabel;
    },
    get state() {
      return getResolved().state;
    },
  };
}

export interface HovGainUpgradeActionCostOptions {
  readonly cost: HugeNumber;
  readonly purchaseCount?: number;
  readonly simultaneousPurchase?: {
    readonly purchaseCount: number;
    readonly totalCost: HugeNumber;
  };
}

export function formatHovGainUpgradeActionCost(
  options: HovGainUpgradeActionCostOptions,
): string {
  const label = formatHovGainUpgradeCostLabel(options, "compact");
  if (options.simultaneousPurchase) {
    const messages = getMessages().hovView.upgradeTooltip;
    return `${label}: ${messages.cost(formatForContext(options.simultaneousPurchase.totalCost, "compact"))}`;
  }
  return `${label}: ${formatHovGainUpgradeCost(options.cost, options.purchaseCount, "compact")}`;
}

export function formatHovGainUpgradeBlockedActionCost(
  reason: HovGainUpgradePurchaseBlockedReason,
): string {
  const messages = getMessages().hovView.upgradeTooltip;
  return `${messages.costLabel}: ${formatHovGainUpgradeBlockedCost(reason)}`;
}

function formatHovGainUpgradeCostLabel(
  input: Pick<HovGainUpgradeTooltipInput, "simultaneousPurchase">,
  context: "compact" | "tooltip",
): string {
  const messages = getMessages().hovView.upgradeTooltip;
  if (input.simultaneousPurchase) {
    return messages.costLabelBatch(
      formatForContext(input.simultaneousPurchase.purchaseCount, context),
    );
  }
  return messages.costLabel;
}

function formatHovGainUpgradeTooltipCost(
  input: HovGainUpgradeTooltipInput,
  context: "compact" | "tooltip",
): string {
  if (input.simultaneousPurchase) {
    const messages = getMessages().hovView.upgradeTooltip;
    return messages.cost(
      formatForContext(input.simultaneousPurchase.totalCost, context),
    );
  }
  return formatHovGainUpgradeCost(input.cost, input.purchaseCount, context);
}

function formatHovGainUpgradeBlockedCost(
  reason: HovGainUpgradePurchaseBlockedReason,
): string {
  switch (reason.type) {
    case "wall":
      return getMessages().hovView.upgradeTooltip.wallBlockedCost;
  }
}

function formatHovGainUpgradeBlockedState(
  reason: HovGainUpgradePurchaseBlockedReason,
): string {
  switch (reason.type) {
    case "wall":
      return getMessages().hovView.upgradeTooltip.stateWallBlocked[
        reason.wallId
      ];
  }
}

function formatHovGainUpgradeTitle(
  kind: HovGainUpgradeTooltipKind,
  enhancementLevel: number,
): string {
  const messages = getMessages().hovView.upgradeTooltip;
  const baseTitle =
    kind.type === "global"
      ? messages.global.title
      : messages.cell.title(kind.displayIndex);
  return `${baseTitle} (${enhancementLevel})`;
}

function formatHovGainUpgradeSummary(kind: HovGainUpgradeTooltipKind): string {
  const messages = getMessages().hovView.upgradeTooltip;
  if (kind.type === "global") {
    return messages.global.summary;
  }
  return messages.cell.summary(kind.displayIndex);
}

function formatHovGainUpgradeAfterLabel(
  kind: HovGainUpgradeTooltipKind,
): string {
  const messages = getMessages().hovView.upgradeTooltip;
  if (kind.type === "global") {
    return messages.global.afterLabel;
  }
  return messages.cell.afterLabel(kind.displayIndex);
}

function formatHovGainUpgradeEffect(input: HovGainUpgradeTooltipInput): string {
  if (input.effect.type === "enhancement-levels") {
    return formatHovGainUpgradeEnhancementLevelEffect(input.effect.count);
  }

  if (input.effect.type === "multiplier") {
    return formatHovGainUpgradeMultiplierEffect(
      input.kind,
      input.effect.multiplier,
    );
  }

  const delta = input.nextValue.sub(input.currentValue);
  return formatHovGainUpgradeDeltaEffect(
    input.kind,
    formatForContext(delta, "tooltip"),
  );
}

function formatHovGainUpgradeEnhancementLevelEffect(count: number): string {
  const messages = getMessages().hovView.upgradeTooltip;
  return messages.effectEnhancementLevels(
    formatForContext(count, "compact"),
    count,
  );
}

function formatHovGainUpgradeDeltaEffect(
  kind: HovGainUpgradeTooltipKind,
  delta: string,
): string {
  const messages = getMessages().hovView.upgradeTooltip;
  if (kind.type === "global") {
    return messages.global.effectDelta(delta);
  }
  return messages.cell.effectDelta(kind.displayIndex, delta);
}

function formatHovGainUpgradeMultiplierEffect(
  kind: HovGainUpgradeTooltipKind,
  multiplier: string,
): string {
  const messages = getMessages().hovView.upgradeTooltip;
  if (kind.type === "global") {
    return messages.global.effectMultiplier(multiplier);
  }
  return messages.cell.effectMultiplier(kind.displayIndex, multiplier);
}

export function formatHovGainUpgradeMilestoneMultiplier(): string {
  return `x${UPGRADE_MILESTONE_MULTIPLIER}`;
}

function formatHovGainUpgradeCost(
  cost: HugeNumber,
  purchaseCount: number | undefined,
  context: "compact" | "tooltip",
): string {
  const messages = getMessages().hovView.upgradeTooltip;
  const normalizedPurchaseCount =
    purchaseCount === undefined || !Number.isFinite(purchaseCount)
      ? 1
      : Math.max(1, Math.floor(purchaseCount));
  const formattedCost = formatForContext(cost, context);
  if (normalizedPurchaseCount <= 1) {
    return messages.cost(formattedCost);
  }
  return messages.batchCost(
    formattedCost,
    formatForContext(normalizedPurchaseCount, context),
  );
}
